import {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
} from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import {
  THEME_COLORS_DARK,
  THEME_COLORS_LIGHT,
  THEME_COLORS_CUSTOM,
  getBaseKey,
} from "@/constants/themeColors";
import { useTheme as useNextTheme } from "next-themes";

interface ThemeContextType {
  isLoading: boolean;
}

const ThemeContext = createContext<ThemeContextType>({
  isLoading: false,
});

export function useTheme() {
  return useContext(ThemeContext);
}

interface ThemeProviderProps {
  children: ReactNode;
}

// CSS variable mapping (base key to CSS variable)
const cssVarMap: Record<string, string> = {
  theme_color_background: "--background",
  theme_color_foreground: "--foreground",
  theme_color_primary: "--primary",
  theme_color_primary_light: "--primary-light",
  theme_color_primary_dark: "--primary-dark",
  theme_color_primary_foreground: "--primary-foreground",
  theme_color_secondary: "--secondary",
  theme_color_secondary_foreground: "--secondary-foreground",
  theme_color_accent: "--accent",
  theme_color_accent_foreground: "--accent-foreground",
  theme_color_muted: "--muted",
  theme_color_muted_foreground: "--muted-foreground",
  theme_color_card: "--card",
  theme_color_card_foreground: "--card-foreground",
  theme_color_border: "--border",
  theme_color_destructive: "--destructive",
  theme_color_destructive_foreground: "--destructive-foreground",
  theme_color_success: "--success",
  theme_color_success_foreground: "--success-foreground",
  theme_color_warning: "--warning",
  theme_color_warning_foreground: "--warning-foreground",
  theme_color_info: "--info",
  theme_color_info_foreground: "--info-foreground",
  theme_color_navbar_background: "--navbar-background",
  theme_color_navbar_foreground: "--navbar-foreground",
  theme_color_navbar_border: "--navbar-border",
  theme_color_footer_background: "--footer-background",
  theme_color_footer_foreground: "--footer-foreground",
  theme_color_footer_muted: "--footer-muted",
};

export function ThemeProvider({ children }: ThemeProviderProps) {
  const queryClient = useQueryClient();
  const { resolvedTheme } = useNextTheme();
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  // Fetch all theme colors (dark, light, and custom)
  const { data: allColors, isLoading } = useQuery({
    queryKey: ["all-theme-colors"],
    queryFn: async () => {
      const allKeys = [
        ...THEME_COLORS_DARK.map((c) => c.key),
        ...THEME_COLORS_LIGHT.map((c) => c.key),
        ...THEME_COLORS_CUSTOM.map((c) => c.key),
      ];

      const { data, error } = await supabase
        .from("app_text_settings")
        .select("key, value")
        .in("key", allKeys);

      if (error) throw error;

      const darkColors: Record<string, string> = {};
      const lightColors: Record<string, string> = {};
      const customColors: Record<string, string> = {};

      data?.forEach((item) => {
        if (item.value) {
          if (item.key.endsWith("_custom")) {
            customColors[item.key] = item.value;
          } else if (item.key.endsWith("_light_mode")) {
            lightColors[item.key] = item.value;
          } else {
            darkColors[item.key] = item.value;
          }
        }
      });

      // Fill in defaults for dark mode
      THEME_COLORS_DARK.forEach((color) => {
        if (!darkColors[color.key]) {
          darkColors[color.key] = color.defaultValue;
        }
      });

      // Fill in defaults for light mode
      THEME_COLORS_LIGHT.forEach((color) => {
        if (!lightColors[color.key]) {
          lightColors[color.key] = color.defaultValue;
        }
      });

      // Fill in defaults for custom mode
      THEME_COLORS_CUSTOM.forEach((color) => {
        if (!customColors[color.key]) {
          customColors[color.key] = color.defaultValue;
        }
      });

      return { darkColors, lightColors, customColors };
    },
    staleTime: Infinity,
    gcTime: Infinity,
  });

  // Apply theme colors to CSS variables based on resolved theme
  useEffect(() => {
    if (!allColors || !mounted) return;

    const root = document.documentElement;

    // Select the appropriate color set based on resolved theme
    let colorsToApply: Record<string, string>;
    if (resolvedTheme === "custom") {
      colorsToApply = allColors.customColors;
    } else if (resolvedTheme === "light") {
      colorsToApply = allColors.lightColors;
    } else {
      colorsToApply = allColors.darkColors;
    }

    // Apply colors to CSS variables
    Object.entries(colorsToApply).forEach(([key, value]) => {
      // Get base key (strip _light_mode or _custom suffix if present)
      const baseKey = getBaseKey(key);
      const cssVar = cssVarMap[baseKey];

      if (cssVar) {
        root.style.setProperty(cssVar, value);
      }
    });
  }, [allColors, resolvedTheme, mounted]);

  return (
    <ThemeContext.Provider value={{ isLoading }}>
      {children}
    </ThemeContext.Provider>
  );
}
